<template>
  <!-- prettier-ignore -->
  <div
    class="PartnerWidget"
    :class="classes"
  >
    <component
      :is="views[screen]"
      v-if="ready && languageLoaded"
      class="PartnerWidget-container"
      v-bind="screenProps"
      v-on="screenEvents"
    />
    <loading-alt v-else />
    <div id="modals" />
    <the-snackbar />
  </div>
</template>

<script setup>
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'

import usePartnerWidget from '@/hooks/usePartnerWidget'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

import Bundle from '@/modules/PartnerWidget/views/Bundle'
import Detail from '@/modules/PartnerWidget/views/Detail'
import Entry from '@/modules/PartnerWidget/views/Entry'
import Error from '@/modules/PartnerWidget/views/Error'
import LoadingAlt from '@/components/Container/LoadingAlt'
import TheSnackbar from '@/components/Basic/Snackbar'

import '@/assets/css/import.css'

// HOOKS
const { getQueryParams, partnerWidgetDisplayMode, partnerWidgetTheme } = usePartnerWidget()
const {
  languageLoaded,
  loadBundle,
  ready,
  screen,
  screensDefinition,
  screensDefinitionInit,
  setBundleId,
  setDisplayMode,
  setLanguage,
  setTheme,
} = usePartnerWidgetBundleLoader()

// INIT
const views = {
  Bundle,
  Detail,
  Error,
  Entry,
}
const props = defineProps({
  bundleId: {
    type: String,
    default: '',
  },
  displayMode: {
    type: String,
    default: '',
  },
  language: {
    type: String,
    default: 'de',
  },
  theme: {
    type: String,
    default: '',
  },
})

// DATA
const screenEvents = ref({})
const screenProps = ref({})

// COMPUTED
const classes = computed(() => {
  return [`theme-${partnerWidgetTheme.value}`, partnerWidgetDisplayMode.value, `screen-${screen.value}`]
})

// METHODS
function init() {
  setBundleId(props.bundleId)
  setDisplayMode(props.displayMode)
  setLanguage(props.language)
  setTheme(props.theme)
}

// WATCHERS
watch(
  () => screen,
  () => {
    if (screen.value) {
      screenEvents.value = screensDefinition.value[screen.value].events
      screenProps.value = screensDefinition.value[screen.value].props
    }
  },
  { deep: true }
)

watch(
  () => screensDefinition,
  () => {
    screenEvents.value = screensDefinition.value[screen.value].events
    screenProps.value = screensDefinition.value[screen.value].props
  },
  { deep: true }
)

// Props watchers
watch(
  () => props.bundleId,
  value => {
    if (value) {
      setBundleId(value)
    }
  }
)

watch(
  () => props.displayMode,
  value => {
    if (value) {
      setDisplayMode(value)
    }
  }
)

watch(
  () => props.language,
  value => {
    if (value) {
      setLanguage(value)
    }
  }
)

watch(
  () => props.theme,
  value => {
    if (value) {
      setTheme(value)
    }
  }
)

// LIFECYCLE HOOKS
onBeforeMount(() => {
  init()
})

onMounted(() => {
  screensDefinitionInit()
  loadBundle(getQueryParams())
})
</script>

<style>
* {
  box-sizing: border-box;
}

.tippy-box {
  border-radius: 0;
  background-color: var(--on-surface);

  .tippy-content {
    padding: 15px;

    ul {
      padding-left: 40px;

      li {
        color: var(--on-primary);
      }
    }
  }
}

.PartnerWidget {
  font-family: 'Open Sans', sans-serif;
  margin: 0 auto;
  position: relative;
  border: 1px solid var(--outline-variant);

  em {
    font-style: normal;
    color: var(--secondary);
  }

  /* type dependant styling */
  &.narrow {
    padding: var(--fixed-spacing-fix-04);
    width: 300px;
    max-width: 300px;
    height: 600px;
    max-height: 600px;

    @media screen and (max-width: 299px) {
      min-width: 200px;
      width: 100vw;

      &.screen-Entry {
        padding: var(--fixed-spacing-fix-04);
      }
    }
  }

  &.wide {
    height: 100vw;
    min-height: 300px;
    max-height: 600px;
    width: 980px;
    padding: var(--fixed-spacing-fix-05);
  }
}

@media screen and (max-width: 299px) {
  .PartnerWidget .sanitas-button {
    font-size: var(--pw-font-size-small);
  }
}
</style>

<style name="short">
.PartnerWidget.short {
  height: 400px;
  max-width: 250px;

  &.screen-Entry {
    padding: var(--fixed-spacing-fix-04);
  }
}
</style>
